import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { assertNever } from '@client/new/helpers/types';

import { isReady, unmetCount } from '../../model/programs.readiness';
import { ProgramReadiness, ProgramStateKind } from '../../model/programs.types';

import { ReadinessIndicator } from './ReadinessIndicator';
import * as styles from './StatusTag.css';

export const toStateLabel = (state: ProgramStateKind): string => {
  switch (state) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    case 'archived':
      return 'Archived';
    default:
      return assertNever(state);
  }
};

const toReadinessAriaSuffix = (readiness: ProgramReadiness): string =>
  isReady(readiness)
    ? 'ready to publish'
    : `${unmetCount(readiness)} criteria missing`;

const toReadinessTooltipText = (
  readiness: ProgramReadiness,
  clickable: boolean,
): string => {
  if (isReady(readiness)) return 'All publication criteria met';
  const summary = `${unmetCount(readiness)} publication criteria missing`;
  return clickable ? `${summary} — click for details` : summary;
};

type StatusTagProps = {
  state: ProgramStateKind;
  /**
   * When provided, integrates a readiness layer (progress ring / solid
   * check) into the badge. `null` means the snapshot has not been computed
   * yet — the badge renders without the readiness layer in that case (no
   * false "not ready" signal).
   */
  readiness?: ProgramReadiness | null;
  /**
   * When provided alongside non-null `readiness`, the badge becomes a
   * clickable button anchoring the readiness popover. The handler receives
   * the badge element so the popover can be positioned relative to it.
   */
  onReadinessClick?: (anchor: HTMLElement) => void;
};

export const StatusTag = ({
  state,
  readiness,
  onReadinessClick,
}: StatusTagProps) => {
  const label = toStateLabel(state);
  const hasReadiness = readiness != null;
  // The readiness indicator (progress ring / solid check) is only
  // meaningful on Draft pills — for Published/Archived the program's
  // lifecycle is already decided and "is it ready to publish?" doesn't
  // apply. The click-to-open-popover affordance still works for any
  // state when readiness data is present (user can inspect criteria).
  const showReadinessLayer = hasReadiness && state === 'draft';

  // Plain badge — no readiness layer, no popover — when snapshot is
  // missing (pre-backfill program). Published/Archived without an
  // indicator also short-circuit through this branch when no
  // `onReadinessClick` is provided.
  if (!hasReadiness) {
    return <span className={styles.tag({ state })}>{label}</span>;
  }

  const ariaLabel = `Status: ${label}, ${toReadinessAriaSuffix(readiness)}`;
  const clickable = onReadinessClick != null;
  const tooltipText = toReadinessTooltipText(readiness, clickable);

  const inner = (
    <span className={styles.contents}>
      {showReadinessLayer ? (
        <ReadinessIndicator readiness={readiness} size={14} />
      ) : null}
      {label}
    </span>
  );

  if (clickable) {
    return (
      <Tooltip
        arrow
        variant="dark"
        title={tooltipText}
        placement="top"
        tooltipProps={{ maxWidth: 220 }}
      >
        <button
          type="button"
          aria-label={ariaLabel}
          className={styles.tag({ state, interactive: true })}
          onClick={(event) => onReadinessClick(event.currentTarget)}
        >
          {inner}
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      arrow
      variant="dark"
      title={tooltipText}
      placement="top"
      tooltipProps={{ maxWidth: 220 }}
    >
      <span className={styles.tag({ state })} role="img" aria-label={ariaLabel}>
        {inner}
      </span>
    </Tooltip>
  );
};
